import axios from 'axios';
import { BASE_URL } from '../components/Constant';

const authHeader = () => ({
  // eslint-disable-next-line no-undef
  authorization: `${localStorage.getItem('token')}`
});

// ---------------------------------------------------------------------------
// READ
// ---------------------------------------------------------------------------

/** Tasks assigned to the logged-in user ("Assigned to Me" tab) */
function getMyTask() {
  return axios.get(BASE_URL + 'task/getMyTask', { headers: authHeader() });
}

/** Tasks created by the logged-in user ("Created by Me" tab) */
function getCreatedByMe() {
  return axios.get(BASE_URL + 'task/getCreatedByMe', { headers: authHeader() });
}

/** All tasks – admin / Total Tasks view */
function getAllTasks(filters = {}) {
  return axios.get(BASE_URL + 'task/tasks', { headers: authHeader(), params: filters });
}

function getDependencyOptions() {
  return axios.get(BASE_URL + 'task/dependencyOptions', { headers: authHeader() });
}

function getExternalContacts() {
  return axios.get(BASE_URL + 'task/externalContacts', { headers: authHeader() });
}

/** Single task by ID */
function getTaskById(taskId) {
  return axios.get(BASE_URL + `task/taskById/${taskId}`, { headers: authHeader() });
}

// ---------------------------------------------------------------------------
// CREATE
// ---------------------------------------------------------------------------
function createTask(taskObj, taskSource) {
  return axios.post(BASE_URL + 'task/createTask', { taskObj, taskSource }, { headers: authHeader() });
}

// ---------------------------------------------------------------------------
// UPDATE
// ---------------------------------------------------------------------------
function markTaskViewed(taskId) {
  return axios.put(BASE_URL + `task/markViewed/${taskId}`, {}, { headers: authHeader() });
}

function updateTask(taskId, changes, taskSource = 'myTask') {
  return axios.put(BASE_URL + `task/updateTask/${taskId}`, { changes, taskSource }, { headers: authHeader() });
}

function updateTaskDependencies(taskId, dependencies) {
  return axios.put(BASE_URL + `task/updateDependencies/${taskId}`, { dependencies }, { headers: authHeader() });
}

function updateAssignedTaskStatus(
  taskId,
  { status, pendingReason, pendingComment, dueDate, supportingStaff, externalRecipients }
) {
  return axios.put(
    BASE_URL + `task/assignedTaskStatus/${taskId}`,
    { status, pendingReason, pendingComment, dueDate, supportingStaff, externalRecipients },
    { headers: authHeader() }
  );
}

function sendTaskReminders(taskIds, taskSource) {
  return axios.post(BASE_URL + `task/sendReminders/${taskSource}`, { taskIds }, { headers: authHeader() });
}

function completeCreatedTask(taskId, { actualHours, completionNotes } = {}) {
  return axios.put(
    BASE_URL + `task/completeCreatedTask/${taskId}`,
    { actualHours, completionNotes },
    { headers: authHeader() }
  );
}

function addAttachments(taskId, files) {
  // eslint-disable-next-line no-undef
  const formData = new FormData();
  Array.from(files || []).forEach((file) => formData.append('files', file));

  return axios.post(BASE_URL + `task/addAttachments/${taskId}`, formData, {
    headers: {
      ...authHeader(),
      'Content-Type': 'multipart/form-data'
    }
  });
}

// ---------------------------------------------------------------------------
// WORKFLOW
// ---------------------------------------------------------------------------
function submitForApproval(taskId, { completionNotes, completionAttachments, actualHours } = {}) {
  return axios.put(
    BASE_URL + `task/submitForApproval/${taskId}`,
    { completionNotes, completionAttachments, actualHours },
    { headers: authHeader() }
  );
}

function approveTask(taskId, { approvalNotes } = {}) {
  return axios.put(BASE_URL + `task/approveTask/${taskId}`, { approvalNotes }, { headers: authHeader() });
}

function rejectTask(taskId, { rejectionReason } = {}) {
  return axios.put(BASE_URL + `task/rejectTask/${taskId}`, { rejectionReason }, { headers: authHeader() });
}

// ---------------------------------------------------------------------------
// COMMENTS
// ---------------------------------------------------------------------------
function addComment(taskId, comment, files = []) {
  // eslint-disable-next-line no-undef
  const formData = new FormData();
  formData.append('comment', comment);
  Array.from(files).forEach((file) => formData.append('files', file));

  return axios.post(BASE_URL + `task/addComment/${taskId}`, formData, {
    headers: {
      ...authHeader(),
      'Content-Type': 'multipart/form-data'
    }
  });
}

// ---------------------------------------------------------------------------
// DELETE
// ---------------------------------------------------------------------------
function deleteTask(taskId) {
  return axios.delete(BASE_URL + `task/deleteTask/${taskId}`, { headers: authHeader() });
}

/** Employee task notification preference */
// ---------------------------------------------------------------------------
// CREATE
// ---------------------------------------------------------------------------
function createTaskNotificationPreference(notificationPreference) {
  return axios.post(
    BASE_URL + 'task/createNotificationPreference',
    { notificationPreference },
    { headers: authHeader() }
  );
}

// ---------------------------------------------------------------------------
// READ
// ---------------------------------------------------------------------------
function getMyNotificationPreference() {
  return axios.get(BASE_URL + 'task/getMyNotificationPreference', { headers: authHeader() });
}

// ---------------------------------------------------------------------------
// UPDATE
// ---------------------------------------------------------------------------
function updateTaskNotificationPreference(notificationPreference) {
  return axios.put(
    BASE_URL + 'task/updateNotificationPreference',
    { notificationPreference },
    { headers: authHeader() }
  );
}

const taskService = {
  getMyTask,
  markTaskViewed,
  getCreatedByMe,
  getAllTasks,
  getDependencyOptions,
  getExternalContacts,
  getTaskById,
  createTask,
  updateTask,
  updateTaskDependencies,
  updateAssignedTaskStatus,
  sendTaskReminders,
  completeCreatedTask,
  addAttachments,
  submitForApproval,
  approveTask,
  rejectTask,
  addComment,
  deleteTask,
  createTaskNotificationPreference,
  getMyNotificationPreference,
  updateTaskNotificationPreference
};

export default taskService;
